import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { ArrowLeft, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { messages } from '@/i18n';
import { useApi } from '@/api/apiContext';
import {
  inviteUserMutationOptions,
  userOrganisationsQueryOptions,
  usersListQueryKey,
  type InviteUserRequest,
} from '@/api/endpoints/users';
import { DASHBOARD_ROLES, type DashboardRole } from '@/types/session';

interface InviteDraft {
  displayName: string;
  email: string;
  userType: DashboardRole;
  organisationId: string;
}

const EMPTY_DRAFT: InviteDraft = {
  displayName: '',
  email: '',
  userType: 'Driver',
  organisationId: '',
};

export function InviteUserScreen() {
  const t = messages.access;
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inviteMutation = useMutation(inviteUserMutationOptions(api));
  const [draft, setDraft] = useState<InviteDraft>(EMPTY_DRAFT);

  // The server is the source of truth for which roles need an organisation at
  // all — it returns organisationType: null with an empty items list for a
  // role that takes none, rather than the client guessing from a local map.
  const orgQuery = useQuery(userOrganisationsQueryOptions(api, draft.userType));
  const needsOrganisation = orgQuery.data ? orgQuery.data.organisationType !== null : false;
  const organisationOptions = orgQuery.data?.items ?? [];

  // Derived rather than synced via an effect: a stale key left over from the
  // previous role (or the initial empty draft) falls back to the first
  // option in the *current* list, so nothing has to reset draft state when
  // the role changes or the options finish loading.
  const selectedOrganisationId = organisationOptions.some((o) => o.key === draft.organisationId)
    ? draft.organisationId
    : (organisationOptions[0]?.key ?? '');

  function submitInvite() {
    const displayName = draft.displayName.trim();
    const email = draft.email.trim();
    if (!displayName || !email) return;
    if (needsOrganisation && !selectedOrganisationId) return;

    const body: InviteUserRequest = {
      displayName,
      email,
      userType: draft.userType,
      ...(needsOrganisation
        ? {
            organisationType: orgQuery.data!.organisationType!,
            organisationKey: selectedOrganisationId,
          }
        : {}),
    };

    inviteMutation.mutate(body, {
      onSuccess: () => {
        // Invalidate the whole list prefix rather than writing the new row into
        // one exact key: the admin may have arrived from a filtered or
        // paginated view, and every cached page's total is stale either way.
        void queryClient.invalidateQueries({ queryKey: usersListQueryKey });
        void navigate('..');
      },
    });
  }

  return (
    <section className="mx-auto max-w-2xl">
      <Link
        to=".."
        className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1.5 text-sm"
      >
        <ArrowLeft className="size-4" aria-hidden />
        {t.backToUsers}
      </Link>

      <h1 className="mb-6 text-2xl font-semibold tracking-tight">{t.inviteFormTitle}</h1>

      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              {t.inviteFormName}
              <input
                className="border-border bg-secondary/40 focus-visible:ring-ring h-9 rounded-md border px-3 text-sm outline-none focus-visible:ring-2"
                value={draft.displayName}
                onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))}
                placeholder="Jane Doe"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              {t.inviteFormEmail}
              <input
                type="email"
                className="border-border bg-secondary/40 focus-visible:ring-ring h-9 rounded-md border px-3 text-sm outline-none focus-visible:ring-2"
                value={draft.email}
                onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                placeholder="jane.doe@example.com"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              {t.inviteFormUserType}
              <select
                className="border-border bg-secondary/40 focus-visible:ring-ring h-9 rounded-md border px-3 text-sm outline-none focus-visible:ring-2"
                value={draft.userType}
                onChange={(e) => {
                  const userType = e.target.value as DashboardRole;
                  setDraft((d) => ({ ...d, userType, organisationId: '' }));
                }}
              >
                {DASHBOARD_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {messages.roles[role]}
                  </option>
                ))}
              </select>
            </label>
            {orgQuery.isLoading ? (
              <p className="text-muted-foreground self-end pb-2 text-sm">{t.organisationLoading}</p>
            ) : needsOrganisation ? (
              <label className="flex flex-col gap-1 text-sm">
                {t.inviteFormOrganisation}
                <select
                  className="border-border bg-secondary/40 focus-visible:ring-ring h-9 rounded-md border px-3 text-sm outline-none focus-visible:ring-2"
                  value={selectedOrganisationId}
                  onChange={(e) => setDraft((d) => ({ ...d, organisationId: e.target.value }))}
                >
                  {organisationOptions.map((org) => (
                    <option key={org.key} value={org.key}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
          {orgQuery.isError ? (
            <Alert variant="destructive" className="mt-4">
              <Info aria-hidden />
              <AlertDescription>{t.organisationError}</AlertDescription>
            </Alert>
          ) : null}
          {inviteMutation.isError ? (
            <Alert variant="destructive" className="mt-4">
              <Info aria-hidden />
              <AlertDescription>{t.inviteFormError}</AlertDescription>
            </Alert>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" asChild>
              <Link to="..">{t.inviteFormCancel}</Link>
            </Button>
            <Button
              onClick={submitInvite}
              disabled={
                !draft.displayName.trim() ||
                !draft.email.trim() ||
                orgQuery.isLoading ||
                orgQuery.isError ||
                (needsOrganisation && !selectedOrganisationId) ||
                inviteMutation.isPending
              }
            >
              {inviteMutation.isPending ? t.inviteFormSending : t.inviteFormSend}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
